import { importLibrary, setOptions } from '@googlemaps/js-api-loader';

declare global {
  interface Window {
    __PADDIQ_GOOGLE_MAPS_KEY__?: string;
    gm_authFailure?: () => void;
  }
}

let loadPromise: Promise<void> | null = null;
let placesLoadPromise: Promise<void> | null = null;
let configuredKey: string | null = null;
let mapsAuthFailed = false;
let mapsLibrariesLoaded = false;
const authFailureListeners = new Set<() => void>();

type GoogleMapsLoadOptions = {
  /** Load Places library for address autocomplete (defaults to on in Vite dev). */
  includePlaces?: boolean;
};

function shouldIncludePlaces(options?: GoogleMapsLoadOptions) {
  if (options?.includePlaces !== undefined) {
    return options.includePlaces;
  }

  return import.meta.env.DEV;
}

async function ensurePlacesLibraryLoaded() {
  if (!window.google?.maps?.places) {
    await importLibrary('places');
  }
}

/** Load the Maps JavaScript API once per page (shared across track map instances). */
export function loadGoogleMaps(apiKey: string, options?: GoogleMapsLoadOptions): Promise<void> {
  if (mapsAuthFailed) {
    return Promise.reject(new Error('Google Maps authentication failed'));
  }

  const includePlaces = shouldIncludePlaces(options);

  if (loadPromise && configuredKey === apiKey) {
    if (!includePlaces) {
      return loadPromise;
    }

    if (!placesLoadPromise) {
      placesLoadPromise = loadPromise.then(() => ensurePlacesLibraryLoaded()).catch((error) => {
        placesLoadPromise = null;
        throw error;
      });
    }

    return placesLoadPromise;
  }

  if (configuredKey !== apiKey) {
    loadPromise = null;
    placesLoadPromise = null;
    configuredKey = apiKey;
    setOptions({ key: apiKey, v: 'quarterly' });
    window.__PADDIQ_GOOGLE_MAPS_KEY__ = apiKey;
  }

  const libraries: Array<Promise<unknown>> = [
    importLibrary('maps'),
    importLibrary('marker').catch(() => null),
  ];
  if (includePlaces) {
    libraries.push(importLibrary('places').catch(() => null));
  }

  loadPromise = Promise.all(libraries)
    .then(() => {
      mapsAuthFailed = false;
      mapsLibrariesLoaded = true;
      return undefined;
    })
    .catch((error) => {
      loadPromise = null;
      throw error;
    });

  return loadPromise;
}

export function mapsAuthHasFailed() {
  return mapsAuthFailed;
}

/** Clears a prior gm_authFailure so Maps can be retried without a full reload (dev). */
export function resetMapsAuthFailure() {
  mapsAuthFailed = false;
  mapsLibrariesLoaded = false;
  loadPromise = null;
  placesLoadPromise = null;
}

export function onMapsAuthFailure(listener: () => void) {
  authFailureListeners.add(listener);
  return () => {
    authFailureListeners.delete(listener);
  };
}

export function registerGoogleMapsAuthFailureHandler() {
  if (typeof window === 'undefined') {
    return;
  }

  window.gm_authFailure = () => {
    mapsAuthFailed = true;
    mapsLibrariesLoaded = false;
    loadPromise = null;
    placesLoadPromise = null;
    for (const listener of authFailureListeners) {
      listener();
    }
  };
}

export function mapsLocalhostReferrerFix(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const { hostname, port } = window.location;
  if (!port || (hostname !== 'localhost' && hostname !== '127.0.0.1')) {
    return null;
  }

  return `http://localhost:${port}/* and http://127.0.0.1:${port}/*`;
}

export function mapsReferrerHint(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const { hostname, port, pathname } = window.location;

  // Google Maps browser keys accept localhost referrers but often reject 127.0.0.1
  // even when http://127.0.0.1:PORT/* is on the allowlist.
  if (hostname === '127.0.0.1' && port) {
    return `http://localhost:${port}${pathname}`;
  }

  if (hostname === 'localhost' && port === '5175') {
    return null;
  }

  if (hostname === 'localhost' && port === '5173') {
    return 'http://localhost:5175/mobile/';
  }

  if (hostname === 'localhost' && port === '5174') {
    return 'http://localhost:5175/telemetry';
  }

  if (pathname.startsWith('/mobile')) {
    return null;
  }

  return null;
}

export function mapsDevAuthHint(apiKeyTail?: string | null): string {
  const alternate = mapsReferrerHint();
  if (alternate) {
    return `Google Maps is blocked on this URL. Open ${alternate} instead, then hard refresh.`;
  }

  if (typeof window !== 'undefined') {
    const { origin } = window.location;
    const localhostFix = mapsLocalhostReferrerFix();
    const referrerHint = localhostFix ?? `${origin}/*`;
    return `Google Maps blocked on ${origin}. In GCP → Credentials, open the browser key ending …${apiKeyTail ?? '??????'} and allow HTTP referrers ${referrerHint}. Enable Maps JavaScript API, Places API, and Geocoding API, and confirm billing is active on the project.`;
  }

  return `Google Maps blocked for this page. Check the browser key ending …${apiKeyTail ?? '??????'} referrers and billing in GCP.`;
}

export function mapsMissingKeyHint(): string {
  return 'Add VITE_GOOGLE_MAPS_API_KEY to apps/web/.env (see apps/web/.env.example), then restart the dev server.';
}
